import { DateTime } from "luxon";

// Past this distance from now, a timestamp renders as a relative phrase
// ("3 months ago"). Inside it, it keeps its calendar date and clock time.
export const RELATIVE_AFTER_DAYS = 30;

// The i18n-supplied phrases for the recency buckets. Weekday, date and relative
// forms are rendered by Luxon (locale-aware) and need no strings.
export interface SmartTimeLabels {
  justNow: () => string;
  today: (time: string) => string;
  yesterday: (time: string) => string;
}

type SmartTimeKey = "common.time.justNow" | "common.time.today" | "common.time.yesterday";

// `t` is passed in to keep this module framework-free.
export function smartTimeLabels(
  t: (key: SmartTimeKey, named?: Record<string, string | number>) => string,
): SmartTimeLabels {
  return {
    justNow: () => t("common.time.justNow"),
    today: (time) => t("common.time.today", { time }),
    yesterday: (time) => t("common.time.yesterday", { time }),
  };
}

// The recency buckets, resolved once so the label and anything that reasons
// about the label cannot drift apart.
type TimeBucket = "justNow" | "today" | "yesterday" | "weekday" | "dated" | "relative";

function timeBucket(dt: DateTime, now: DateTime): TimeBucket {
  const seconds = now.diff(dt, "seconds").seconds;
  if (seconds >= 0 && seconds < 60) return "justNow";

  const dayDiff = now.startOf("day").diff(dt.startOf("day"), "days").days;
  if (Math.abs(dayDiff) > RELATIVE_AFTER_DAYS) return "relative";
  if (dayDiff === 0) return "today";
  if (dayDiff === 1) return "yesterday";
  if (dayDiff > 1 && dayDiff < 7) return "weekday";
  return "dated";
}

// Buckets whose label names no calendar date. "Tue, 12:10 AM" and "3 months ago"
// leave the hover as the only place it appears. "Today" and "Yesterday" name the
// day in words, so they do not.
const DATELESS_BUCKETS: ReadonlySet<TimeBucket> = new Set<TimeBucket>(["weekday", "relative"]);

// labelHidesDate reports whether smartTimeLabel will render `value` without a
// calendar date. Takes a parsed DateTime too, so a caller that already has one
// skips a reparse.
export function labelHidesDate(value: string | DateTime, now?: DateTime): boolean {
  const dt = typeof value === "string" ? DateTime.fromISO(value) : value;
  // Bail before reading the clock, so a spec that stubs Luxon down to fromISO does not need now().
  if (!dt.isValid) return false;
  return DATELESS_BUCKETS.has(timeBucket(dt, now ?? DateTime.now()));
}

// smartTimeLabel renders a timestamp as a human label:
//   < 1 min            "Just now"
//   same day           "Today, 2:17 PM"
//   day before         "Yesterday, 9:31 AM"
//   rest of the week   "Tue, 12:10 AM"
//   up to 30 days      "8 Jul, 10:10 AM"
//   beyond 30 days     "3 months ago"
// The cutoff applies in both directions, so a distant expiry reads "in 3 months".
// Pair it with `exactTimestamp` on hover and sort on the underlying timestamp.
// `now` is injectable so the buckets stay testable.
export function smartTimeLabel(iso: string, labels: SmartTimeLabels, now: DateTime = DateTime.now()): string {
  const dt = DateTime.fromISO(iso);
  if (!dt.isValid) return "-";

  const bucket = timeBucket(dt, now);
  if (bucket === "justNow") return labels.justNow();

  const time = dt.toLocaleString(DateTime.TIME_SIMPLE);
  switch (bucket) {
    case "relative":
      return dt.toRelative({ base: now }) ?? `${dt.toFormat("d LLL yyyy")}, ${time}`;
    case "today":
      return labels.today(time);
    case "yesterday":
      return labels.yesterday(time);
    case "weekday":
      return `${dt.toFormat("ccc")}, ${time}`;
    default:
      return dt.year === now.year ? `${dt.toFormat("d LLL")}, ${time}` : `${dt.toFormat("d LLL yyyy")}, ${time}`;
  }
}

// The hover panel's contents. The table label shows either the calendar date or
// how long ago, never both, so the panel carries the other half, plus UTC for
// anyone lining a row up against server logs.
export interface TimestampDetail {
  exact: string;
  weekday: string;
  // Null under a minute, where Luxon renders "0 seconds ago" and the exact time
  // already says it.
  relative: string | null;
  utc: string;
}

export function timestampDetail(iso: string, now?: DateTime): TimestampDetail | undefined {
  const dt = DateTime.fromISO(iso);
  if (!dt.isValid) return undefined;
  const base = now ?? DateTime.now();
  const withinAMinute = Math.abs(base.diff(dt, "seconds").seconds) < 60;
  return {
    exact: dt.toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS),
    weekday: dt.toFormat("cccc"),
    relative: withinAMinute ? null : dt.toRelative({ base }),
    // Explicit format: toISO's suppressMilliseconds only drops them when they
    // are already zero, so a real timestamp keeps its ".715".
    utc: dt.toUTC().toFormat("yyyy-MM-dd'T'HH:mm:ss'Z'"),
  };
}

// exactTimestamp is the precise form shown on hover. Undefined for an
// unparseable value, which drops the title rather than showing an empty one.
export function exactTimestamp(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined;
  const dt = DateTime.fromISO(iso);
  return dt.isValid ? dt.toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS) : undefined;
}
